/**
 * One running example company, used as the single source of truth for every
 * worked example and chart on the SaaS metrics guide. Keeping all numbers here
 * means the prose and the visuals always reconcile.
 *
 * A company doing $20,000 MRR across 100 customers (ARPA $200), over one month.
 */

export const EXAMPLE = {
  customersStart: 100,
  arpa: 200,
  startingMrr: 20_000,
  newMrr: 4_000, // 20 new customers × $200
  newCustomers: 20,
  expansionMrr: 3_000,
  contractionMrr: 800,
  churnedMrr: 1_200, // 6 customers
  churnedCustomers: 6,
  // Derived
  netNewMrr: 5_000, // 4000 + 3000 − 800 − 1200
  endingMrr: 25_000,
  endingArr: 300_000, // 25_000 × 12
  // Unit economics (illustrative steady-state)
  grossMargin: 0.75,
  avgLifetimeMonths: 40,
  ltv: 6_000, // 200 × 0.75 × 40
  salesAndMarketing: 40_000,
  cac: 2_000, // 40_000 ÷ 20
  cacPaybackMonths: 13, // 2_000 ÷ (200 × 0.75) ≈ 13.3
  newAcv: 48_000, // 4_000 × 12
  asp: 2_400, // 48_000 ÷ 20
  customerChurnRate: 6, // 6 ÷ 100 × 100
  revenueChurnRate: 10, // (1200 + 800) ÷ 20_000 × 100
  grr: 90, // (20_000 − 1_200 − 800) ÷ 20_000 × 100
  nrr: 105, // (20_000 + 3_000 − 800 − 1_200) ÷ 20_000 × 100
  quickRatio: 3.5, // (4_000 + 3_000) ÷ (1_200 + 800)
} as const;

export interface WaterfallBar {
  label: string;
  /** Invisible base offset for the floating bar. */
  base: number;
  /** Visible magnitude of the bar. */
  value: number;
  /** Running total the bar reaches (for tooltip). */
  total: number;
  kind: "total" | "gain" | "loss";
}

/**
 * MRR movement waterfall: Start → +New → +Expansion → −Contraction → −Churn → End.
 * Each step's `base` floats the bar so the tops/bottoms form a bridge.
 */
export const MRR_MOVEMENT: WaterfallBar[] = (() => {
  const { startingMrr, newMrr, expansionMrr, contractionMrr, churnedMrr, endingMrr } = EXAMPLE;
  const afterNew = startingMrr + newMrr;
  const afterExpansion = afterNew + expansionMrr;
  const afterContraction = afterExpansion - contractionMrr;
  const afterChurn = afterContraction - churnedMrr;
  return [
    { label: "Start", base: 0, value: startingMrr, total: startingMrr, kind: "total" },
    { label: "New", base: startingMrr, value: newMrr, total: afterNew, kind: "gain" },
    {
      label: "Expansion",
      base: afterNew,
      value: expansionMrr,
      total: afterExpansion,
      kind: "gain",
    },
    {
      label: "Contraction",
      base: afterContraction,
      value: contractionMrr,
      total: afterContraction,
      kind: "loss",
    },
    { label: "Churn", base: afterChurn, value: churnedMrr, total: afterChurn, kind: "loss" },
    { label: "End", base: 0, value: endingMrr, total: endingMrr, kind: "total" },
  ];
})();

/**
 * Expansion MRR by month — a realistic, choppy series (expansion is lumpy: a big
 * upgrade one month, a quiet one the next) that trends up and lands on the
 * running example's $3,000 expansion month, so the chart and prose reconcile.
 */
export const EXPANSION_MRR = [
  { label: "Jan", value: 1_800 },
  { label: "Feb", value: 1_300 },
  { label: "Mar", value: 2_400 },
  { label: "Apr", value: 2_000 },
  { label: "May", value: 2_600 },
  { label: "Jun", value: EXAMPLE.expansionMrr },
];

/**
 * New MRR by month — acquisition revenue, lumpy but trending up, landing on the
 * running example's $4,000 new-business month so the chart and prose reconcile.
 */
export const NEW_MRR = [
  { label: "Jan", value: 2_800 },
  { label: "Feb", value: 3_200 },
  { label: "Mar", value: 3_000 },
  { label: "Apr", value: 3_600 },
  { label: "May", value: 3_400 },
  { label: "Jun", value: EXAMPLE.newMrr },
];

/**
 * Contraction MRR by month — a lumpy series (downgrades come in bursts) with no
 * strong trend, landing on the running example's $800 contraction month so the
 * chart and prose reconcile. Shown as positive magnitudes, colored as a loss.
 */
export const CONTRACTION_MRR = [
  { label: "Jan", value: 600 },
  { label: "Feb", value: 900 },
  { label: "Mar", value: 500 },
  { label: "Apr", value: 1_100 },
  { label: "May", value: 700 },
  { label: "Jun", value: EXAMPLE.contractionMrr },
];

/**
 * Churn MRR by month — revenue lost to cancellations, lumpy with no healthy
 * trend, landing on the running example's $1,200 churn month. Positive
 * magnitudes, colored as a loss.
 */
export const CHURN_MRR = [
  { label: "Jan", value: 900 },
  { label: "Feb", value: 1_400 },
  { label: "Mar", value: 1_000 },
  { label: "Apr", value: 1_600 },
  { label: "May", value: 1_100 },
  { label: "Jun", value: EXAMPLE.churnedMrr },
];

/**
 * Reactivation MRR by month — revenue from returning customers, small and lumpy
 * (win-backs are occasional), landing on a $600 month that fits the running
 * example's scale. A gain.
 */
export const REACTIVATION_MRR = [
  { label: "Jan", value: 300 },
  { label: "Feb", value: 500 },
  { label: "Mar", value: 200 },
  { label: "Apr", value: 700 },
  { label: "May", value: 400 },
  { label: "Jun", value: 600 },
];

/**
 * Total MRR by month — the running example's book growing from $20,000 to the
 * $25,000 ending month, a steady climb net of all movements.
 */
export const MRR_TREND = [
  { label: "Jan", value: EXAMPLE.startingMrr },
  { label: "Feb", value: 21_200 },
  { label: "Mar", value: 22_100 },
  { label: "Apr", value: 23_200 },
  { label: "May", value: 24_100 },
  { label: "Jun", value: EXAMPLE.endingMrr },
];

/**
 * Net New Revenue by month — net change in MRR after every movement. Mostly
 * positive with one down month, landing on the running example's $5,000. Colored
 * per bar by sign (gain green, loss red).
 */
export const NET_NEW_REVENUE = [
  { label: "Jan", value: 3_200 },
  { label: "Feb", value: -500 },
  { label: "Mar", value: 4_100 },
  { label: "Apr", value: 2_800 },
  { label: "May", value: 4_600 },
  { label: "Jun", value: EXAMPLE.netNewMrr },
];

/**
 * MRR Growth Rate by month — the period-over-period % change in MRR_TREND. Values
 * reconcile with that series (e.g. 24.1k → 25.0k ≈ 3.7%), naturally slowing as the
 * base grows. Colored per bar by sign.
 */
export const MRR_GROWTH = [
  { label: "Jan", value: 5.0 },
  { label: "Feb", value: 6.0 },
  { label: "Mar", value: 4.2 },
  { label: "Apr", value: 5.0 },
  { label: "May", value: 3.9 },
  { label: "Jun", value: 3.7 },
];

/**
 * Paying accounts by month — the count of accounts with MRR > 0, growing from
 * the running example's 100 as new logos outpace churn.
 */
export const PAYING_ACCOUNTS = [
  { label: "Jan", value: EXAMPLE.customersStart },
  { label: "Feb", value: 104 },
  { label: "Mar", value: 107 },
  { label: "Apr", value: 110 },
  { label: "May", value: 112 },
  { label: "Jun", value: 116 },
];

/**
 * MRR movements as a time series (the app's stacked view): each month stacks
 * gains up (new, expansion, reactivation) and losses down (contraction, churn)
 * from a zero line, with `net` for the overlaid line. Built from the per-movement
 * monthly series above so every chart reconciles. Losses are stored negative.
 */
export const MRR_MOVEMENTS_SERIES = NEW_MRR.map((point, i) => {
  const newMrr = point.value;
  const expansion = EXPANSION_MRR[i].value;
  const reactivation = REACTIVATION_MRR[i].value;
  const contraction = -CONTRACTION_MRR[i].value;
  const churn = -CHURN_MRR[i].value;
  return {
    label: point.label,
    new: newMrr,
    expansion,
    reactivation,
    contraction,
    churn,
    net: newMrr + expansion + reactivation + contraction + churn,
  };
});

/**
 * Paying customers movements as a time series (the app's view): New paying
 * accounts stack up, Churned stack down, with a Net Change line. Net roughly
 * tracks the PAYING_ACCOUNTS trend's month-over-month growth. Churned is negative.
 */
export const PAYING_CUSTOMER_MOVEMENTS_SERIES = [
  { label: "Jan", new: 8, churned: -3 },
  { label: "Feb", new: 7, churned: -3 },
  { label: "Mar", new: 6, churned: -3 },
  { label: "Apr", new: 7, churned: -4 },
  { label: "May", new: 5, churned: -3 },
  { label: "Jun", new: 8, churned: -4 },
].map((m) => ({ ...m, net: m.new + m.churned }));

/**
 * Average per-event amounts by month (dollars). Illustrative, lumpy series that
 * sit at the scale implied by the running example (e.g. avg churn ≈ churn MRR ÷
 * churn events ≈ $200). Single-metric blue in the app.
 */
export const AVG_SALES_PRICE = [
  { label: "Jan", value: 2_200 },
  { label: "Feb", value: 2_500 },
  { label: "Mar", value: 2_300 },
  { label: "Apr", value: 2_600 },
  { label: "May", value: 2_350 },
  { label: "Jun", value: EXAMPLE.asp },
];

/**
 * Blended CAC by month — sales & marketing spend ÷ new paying customers. Trends
 * down to the example's steady-state: $40,000 of S&M ÷ 20 new customers = $2,000.
 */
export const CAC_TREND = [
  { label: "Jan", value: 2_450 },
  { label: "Feb", value: 2_300 },
  { label: "Mar", value: 2_350 },
  { label: "Apr", value: 2_150 },
  { label: "May", value: 2_100 },
  { label: "Jun", value: EXAMPLE.cac },
];

/**
 * LTV:CAC ratio by month — the steady $6,000 LTV over the easing CAC_TREND,
 * improving to the example's healthy 3:1 benchmark.
 */
export const LTV_CAC_RATIO_TREND = [
  { label: "Jan", value: 2.4 },
  { label: "Feb", value: 2.6 },
  { label: "Mar", value: 2.6 },
  { label: "Apr", value: 2.8 },
  { label: "May", value: 2.9 },
  { label: "Jun", value: 3.0 }, // EXAMPLE.ltv ÷ EXAMPLE.cac
];

/**
 * CAC payback by month — CAC_TREND ÷ ($200 ARPA × 75% gross margin = $150 of
 * monthly margin), rounded to whole months and easing to the example's ~13.
 */
export const CAC_PAYBACK_TREND = [
  { label: "Jan", value: 16 },
  { label: "Feb", value: 15 },
  { label: "Mar", value: 16 },
  { label: "Apr", value: 14 },
  { label: "May", value: 14 },
  { label: "Jun", value: EXAMPLE.cacPaybackMonths },
];

/**
 * Gross revenue churn rate by month — (churn + contraction MRR) ÷ starting MRR,
 * landing on the example's 10% (($1,200 + $800) ÷ $20,000).
 */
export const REVENUE_CHURN_RATE = [
  { label: "Jan", value: 11.8 },
  { label: "Feb", value: 11.2 },
  { label: "Mar", value: 11.6 },
  { label: "Apr", value: 10.7 },
  { label: "May", value: 10.4 },
  { label: "Jun", value: EXAMPLE.revenueChurnRate },
];

/**
 * Net revenue churn by month — gross losses minus expansion, as % of starting
 * MRR. Crosses zero into negative churn, landing on the example's −5%
 * (($1,200 + $800 − $3,000) ÷ $20,000 — the mirror of a 105% NRR).
 */
export const NET_REVENUE_CHURN = [
  { label: "Jan", value: 3 },
  { label: "Feb", value: 1.5 },
  { label: "Mar", value: 2 },
  { label: "Apr", value: -1 },
  { label: "May", value: -2.5 },
  { label: "Jun", value: -5 },
];

export interface BenchmarkRange {
  label: string;
  /** Bottom of the healthy range (floats the bar). */
  min: number;
  /** Top of the healthy range. */
  max: number;
}

/**
 * Typical bounce-rate ranges by site type (classic single-page definition) —
 * the bands quoted in the good-bounce-rate guide.
 */
export const BOUNCE_RATE_BENCHMARKS: BenchmarkRange[] = [
  { label: "Ecommerce", min: 20, max: 45 },
  { label: "SaaS / multi-page", min: 26, max: 55 },
  { label: "Landing pages", min: 60, max: 90 },
  { label: "Blogs & content", min: 65, max: 90 },
];

export interface SourceRevenueSplit {
  source: string;
  /** Share of total visits (%). */
  visits: number;
  /** Share of total revenue (%). */
  revenue: number;
}

/**
 * Illustrative traffic-source split — each channel's share of visits vs. its
 * share of revenue, quoted in the traffic-sources-explained guide. Chosen so the
 * visit-rank and revenue-rank order differ: organic leads on visits, paid leads
 * on revenue. Both columns sum to 100%.
 */
export const TRAFFIC_SOURCE_SPLIT: SourceRevenueSplit[] = [
  { source: "Organic", visits: 34, revenue: 27 },
  { source: "Direct", visits: 30, revenue: 21 },
  { source: "Paid", visits: 22, revenue: 34 },
  { source: "Referral", visits: 14, revenue: 18 },
];

/**
 * Typical NRR ranges by ACV segment — the bands quoted in the
 * good-nrr-benchmarks guide. 100% is the compounding threshold.
 */
export const NRR_BENCHMARKS: BenchmarkRange[] = [
  { label: "SMB (<$5k ACV)", min: 90, max: 105 },
  { label: "Mid-market ($5–50k)", min: 100, max: 115 },
  { label: "Enterprise (>$50k)", min: 110, max: 130 },
];

/**
 * Typical monthly logo-churn ranges by ACV segment — the bands quoted in the
 * average-saas-churn-rate guide.
 */
export const CHURN_BENCHMARKS: BenchmarkRange[] = [
  { label: "SMB (<$5k ACV)", min: 3, max: 7 },
  { label: "Mid-market ($5–50k)", min: 1, max: 2.5 },
  { label: "Enterprise (>$50k)", min: 0.3, max: 1 },
];

/**
 * Average ACV by month — ARPA annualised, landing on $2,400 (= $200 ARPA × 12).
 */
export const ACV_TREND = [
  { label: "Jan", value: 2_150 },
  { label: "Feb", value: 2_300 },
  { label: "Mar", value: 2_250 },
  { label: "Apr", value: 2_350 },
  { label: "May", value: 2_450 },
  { label: "Jun", value: 2_400 }, // EXAMPLE.arpa × 12
];

/**
 * SaaS magic number by month — annualised net new revenue ÷ prior-period S&M
 * spend, landing on 1.5 (= $5,000 net new MRR × 12 ÷ $40,000 S&M).
 */
export const MAGIC_NUMBER_TREND = [
  { label: "Jan", value: 0.9 },
  { label: "Feb", value: 1.1 },
  { label: "Mar", value: 1.0 },
  { label: "Apr", value: 1.2 },
  { label: "May", value: 1.4 },
  { label: "Jun", value: 1.5 },
];

/**
 * Burn multiple by month — net burn ÷ net new ARR, improving toward efficient.
 */
export const BURN_MULTIPLE_TREND = [
  { label: "Jan", value: 2.4 },
  { label: "Feb", value: 2.1 },
  { label: "Mar", value: 2.2 },
  { label: "Apr", value: 1.9 },
  { label: "May", value: 1.7 },
  { label: "Jun", value: 1.5 },
];

/** Renewal rate by month — share of contracts up for renewal that renewed. */
export const RENEWAL_RATE_TREND = [
  { label: "Jan", value: 84 },
  { label: "Feb", value: 86 },
  { label: "Mar", value: 85 },
  { label: "Apr", value: 88 },
  { label: "May", value: 90 },
  { label: "Jun", value: 91 },
];

/**
 * One $120,000 two-year contract at three accounting stages: booked in full at
 * signature, half billed up front (year one), a quarter recognized after six
 * months of delivery.
 */
export const DEAL_STAGES = [
  { label: "Bookings (signed)", value: 120_000 },
  { label: "Billings (invoiced)", value: 60_000 },
  { label: "Revenue (recognized)", value: 30_000 },
];

/**
 * Committed MRR by month — MRR minus known upcoming churn plus signed
 * not-yet-live contracts; runs below headline MRR (which lands on $25,000).
 */
export const CMRR_TREND = [
  { label: "Jan", value: 19_000 },
  { label: "Feb", value: 19_800 },
  { label: "Mar", value: 20_400 },
  { label: "Apr", value: 21_200 },
  { label: "May", value: 21_900 },
  { label: "Jun", value: 22_500 },
];

/**
 * Gross margin by month — climbing to the example's steady-state 75%
 * (EXAMPLE.grossMargin) as infrastructure and support costs amortise.
 */
export const GROSS_MARGIN_TREND = [
  { label: "Jan", value: 68 },
  { label: "Feb", value: 70 },
  { label: "Mar", value: 71 },
  { label: "Apr", value: 72 },
  { label: "May", value: 74 },
  { label: "Jun", value: EXAMPLE.grossMargin * 100 },
];

/** DAU/MAU stickiness by month — share of monthly actives who show up daily. */
export const DAU_MAU_TREND = [
  { label: "Jan", value: 22 },
  { label: "Feb", value: 24 },
  { label: "Mar", value: 23 },
  { label: "Apr", value: 26 },
  { label: "May", value: 28 },
  { label: "Jun", value: 29 },
];

/**
 * ARR per employee by month — the example's $300,000 ARR spread over a team
 * growing more slowly than revenue, landing on $25,000 (12 people).
 */
export const ARR_PER_EMPLOYEE = [
  { label: "Jan", value: 18_000 },
  { label: "Feb", value: 19_500 },
  { label: "Mar", value: 20_500 },
  { label: "Apr", value: 22_000 },
  { label: "May", value: 23_500 },
  { label: "Jun", value: 25_000 }, // EXAMPLE.endingArr ÷ 12 employees
];

/**
 * Typical trial-to-paid conversion ranges by signup motion — the bands quoted
 * in the trial-to-paid-benchmarks guide.
 */
export const TRIAL_TO_PAID_BENCHMARKS: BenchmarkRange[] = [
  { label: "Freemium", min: 2, max: 5 },
  { label: "Free trial, no card", min: 8, max: 20 },
  { label: "Free trial, card up front", min: 30, max: 50 },
];

/**
 * Typical activation-rate ranges by product complexity — the bands quoted in
 * the activation-rate-benchmarks guide.
 */
export const ACTIVATION_BENCHMARKS: BenchmarkRange[] = [
  { label: "Simple, single-player", min: 40, max: 60 },
  { label: "Multi-step setup", min: 25, max: 40 },
  { label: "Complex / enterprise", min: 15, max: 30 },
];

/**
 * The mix illusion from the activation-rate-benchmarks guide: segment rates
 * identical across quarters, but shifting the signup mix from 80/20 to 60/40
 * self-serve/sales-assisted lifts the blended rate 6 points on its own.
 */
export const ACTIVATION_MIX = [
  { label: "Self-serve", value: 25 },
  { label: "Sales-assisted", value: 55 },
  { label: "Blended, 80/20 mix", value: 31 },
  { label: "Blended, 60/40 mix", value: 37 },
];

/**
 * Retention curves split by activation — the activation guide's "retention
 * test". Activated accounts hold ~62% at week 8; non-activated collapse to 15%.
 */
export const ACTIVATION_RETENTION_SPLIT = [
  { label: "Week 0", activated: 100, notActivated: 100 },
  { label: "Week 1", activated: 88, notActivated: 62 },
  { label: "Week 2", activated: 80, notActivated: 44 },
  { label: "Week 3", activated: 75, notActivated: 33 },
  { label: "Week 4", activated: 71, notActivated: 26 },
  { label: "Week 5", activated: 68, notActivated: 21 },
  { label: "Week 6", activated: 65, notActivated: 18 },
  { label: "Week 7", activated: 63, notActivated: 16 },
  { label: "Week 8", activated: 62, notActivated: 15 },
];

/**
 * Bridge from Stripe's reported Billing MRR to real MRR — the
 * stripe-mrr-is-wrong guide's hero visual. Ends on EXAMPLE.endingMrr so the
 * guide reconciles with the running example: $23,200 reported, plus uninvoiced
 * usage-based revenue and other processors, minus past-due risk and
 * unconfigured discounts, lands on $25,000.
 */
export const STRIPE_MRR_BRIDGE: WaterfallBar[] = [
  { label: "Stripe MRR", base: 0, value: 23_200, total: 23_200, kind: "total" },
  { label: "Usage-based", base: 23_200, value: 1_500, total: 24_700, kind: "gain" },
  { label: "Other processors", base: 24_700, value: 1_000, total: 25_700, kind: "gain" },
  { label: "Past-due risk", base: 25_300, value: 400, total: 25_300, kind: "loss" },
  { label: "Discounts", base: 25_000, value: 300, total: 25_000, kind: "loss" },
  { label: "Real MRR", base: 0, value: EXAMPLE.endingMrr, total: EXAMPLE.endingMrr, kind: "total" },
];

/**
 * What monthly churn compounds to over a year — 1 − (1 − monthly)¹², the
 * average-saas-churn-rate guide's "monthly numbers anesthetize" argument.
 */
export const CHURN_COMPOUNDING = [
  { label: "1%/mo", value: 11.4 },
  { label: "2%/mo", value: 21.5 },
  { label: "3%/mo", value: 30.6 },
  { label: "5%/mo", value: 46.0 },
  { label: "7%/mo", value: 58.2 },
];

/**
 * Monthly churn rate by customer tenure — young cohorts churn several times
 * faster than tenured ones, so blended churn overstates a fast-grower's leak.
 */
export const CHURN_BY_TENURE = [
  { label: "Months 1–3", value: 8 },
  { label: "Months 4–6", value: 5 },
  { label: "Months 7–12", value: 3.5 },
  { label: "Year 2", value: 2 },
  { label: "Year 3+", value: 1.2 },
];

/**
 * Two companies at the same 110% NRR, decomposed into GRR + expansion — the
 * good-nrr-benchmarks guide's "same NRR, different businesses" argument.
 */
export const NRR_COMPOSITION = [
  { label: "Durable base", grr: 95, expansion: 15 },
  { label: "Expansion-propped", grr: 80, expansion: 30 },
];

/**
 * The whale-concentration test from the good-nrr-benchmarks guide: reported
 * NRR vs the same cohort with the top-5 expansion accounts removed.
 */
export const NRR_WHALE_TEST = [
  { label: "Reported NRR", value: 115 },
  { label: "Without top-5 expansions", value: 96 },
];

/**
 * Rule of 40 score by month — annualized revenue growth rate plus profit
 * margin, in points. Crossing the 40 line means growth and profitability
 * combined clear the bar.
 */
export const RULE_OF_40 = [
  { label: "Jan", value: 31 },
  { label: "Feb", value: 34 },
  { label: "Mar", value: 33 },
  { label: "Apr", value: 37 },
  { label: "May", value: 39 },
  { label: "Jun", value: 42 },
];
export const AVG_EXPANSION_AMOUNT = [
  { label: "Jan", value: 240 },
  { label: "Feb", value: 310 },
  { label: "Mar", value: 280 },
  { label: "Apr", value: 330 },
  { label: "May", value: 290 },
  { label: "Jun", value: 300 },
];
export const AVG_CONTRACTION_AMOUNT = [
  { label: "Jan", value: 150 },
  { label: "Feb", value: 220 },
  { label: "Mar", value: 180 },
  { label: "Apr", value: 250 },
  { label: "May", value: 190 },
  { label: "Jun", value: 200 },
];
export const AVG_CHURN_AMOUNT = [
  { label: "Jan", value: 190 },
  { label: "Feb", value: 230 },
  { label: "Mar", value: 205 },
  { label: "Apr", value: 240 },
  { label: "May", value: 195 },
  { label: "Jun", value: 200 },
];
export const AVG_REACTIVATION_AMOUNT = [
  { label: "Jan", value: 160 },
  { label: "Feb", value: 210 },
  { label: "Mar", value: 180 },
  { label: "Apr", value: 260 },
  { label: "May", value: 200 },
  { label: "Jun", value: 230 },
];

/** Customers retention rate by month (%) — the mirror of the ~6% churn rate. */
export const CUSTOMERS_RETENTION_RATE = [
  { label: "Jan", value: 93 },
  { label: "Feb", value: 92 },
  { label: "Mar", value: 94 },
  { label: "Apr", value: 93 },
  { label: "May", value: 95 },
  { label: "Jun", value: 94 },
];

/** Realized cLTV by tenure month ($) — cumulative revenue actually collected. */
export const REALIZED_CLV = [
  { label: "M1", value: 420 },
  { label: "M2", value: 980 },
  { label: "M3", value: 1_600 },
  { label: "M4", value: 2_300 },
  { label: "M5", value: 3_050 },
  { label: "M6", value: 3_800 },
];

/** Average account lifespan by month (months) — roughly 1 / monthly churn rate. */
export const AVG_ACCOUNT_LIFESPAN = [
  { label: "Jan", value: 15 },
  { label: "Feb", value: 16 },
  { label: "Mar", value: 16 },
  { label: "Apr", value: 17 },
  { label: "May", value: 17 },
  { label: "Jun", value: 17 },
];

/** Average company lifespan by month (months) — companies stick longer than accounts. */
export const AVG_COMPANY_LIFESPAN = [
  { label: "Jan", value: 18 },
  { label: "Feb", value: 19 },
  { label: "Mar", value: 19 },
  { label: "Apr", value: 20 },
  { label: "May", value: 20 },
  { label: "Jun", value: 21 },
];

/** Win-back rate by month (%) — share of churned logos that return within 12 months. */
export const WIN_BACK_RATE = [
  { label: "Jan", value: 18 },
  { label: "Feb", value: 22 },
  { label: "Mar", value: 20 },
  { label: "Apr", value: 25 },
  { label: "May", value: 23 },
  { label: "Jun", value: 26 },
];

/** Median time to return by month (months) — churn to resubscription for the median returner. */
export const MEDIAN_TIME_TO_RETURN = [
  { label: "Jan", value: 4 },
  { label: "Feb", value: 3 },
  { label: "Mar", value: 5 },
  { label: "Apr", value: 3 },
  { label: "May", value: 4 },
  { label: "Jun", value: 3 },
];

/**
 * Net Revenue Retention by month (%) — crosses the 100% line: one soft month
 * below it, then expansion carries it above, landing on the example's 105%.
 */
export const NRR_RATE = [
  { label: "Jan", value: 98 },
  { label: "Feb", value: 102 },
  { label: "Mar", value: 104 },
  { label: "Apr", value: 103 },
  { label: "May", value: 107 },
  { label: "Jun", value: EXAMPLE.nrr },
];

/** Signup-to-paid conversion rate by month (%). */
export const CONVERSION_RATE_TREND = [
  { label: "Jan", value: 12 },
  { label: "Feb", value: 14 },
  { label: "Mar", value: 13 },
  { label: "Apr", value: 16 },
  { label: "May", value: 15 },
  { label: "Jun", value: 17 },
];

/** Activation rate by month (%) — new accounts that reached the activation milestone. */
export const ACTIVATION_RATE_TREND = [
  { label: "Jan", value: 40 },
  { label: "Feb", value: 44 },
  { label: "Mar", value: 42 },
  { label: "Apr", value: 48 },
  { label: "May", value: 46 },
  { label: "Jun", value: 50 },
];

/** Trial-to-paid conversion rate by month (%) — trials that convert within 30 days. */
export const TRIAL_TO_PAID_RATE = [
  { label: "Jan", value: 24 },
  { label: "Feb", value: 28 },
  { label: "Mar", value: 26 },
  { label: "Apr", value: 30 },
  { label: "May", value: 27 },
  { label: "Jun", value: 31 },
];

/** Expansion rate by month (%) — share of paying accounts that expanded. */
export const EXPANSION_RATE = [
  { label: "Jan", value: 8 },
  { label: "Feb", value: 10 },
  { label: "Mar", value: 9 },
  { label: "Apr", value: 12 },
  { label: "May", value: 11 },
  { label: "Jun", value: 13 },
];

/** Signups by month (count of account-created events). */
export const SIGNUPS = [
  { label: "Jan", value: 320 },
  { label: "Feb", value: 380 },
  { label: "Mar", value: 350 },
  { label: "Apr", value: 420 },
  { label: "May", value: 400 },
  { label: "Jun", value: 460 },
];

/** Trials started by month (count of trial-started events). */
export const TRIALS_STARTED = [
  { label: "Jan", value: 180 },
  { label: "Feb", value: 210 },
  { label: "Mar", value: 195 },
  { label: "Apr", value: 240 },
  { label: "May", value: 225 },
  { label: "Jun", value: 260 },
];

/** Median time to activated by month (days) — signup to first realised value. */
export const TIME_TO_ACTIVATED = [
  { label: "Jan", value: 4 },
  { label: "Feb", value: 3 },
  { label: "Mar", value: 4 },
  { label: "Apr", value: 3 },
  { label: "May", value: 3 },
  { label: "Jun", value: 2 },
];

/** Median time to engaged by month (days) — signup to second realised value. */
export const TIME_TO_ENGAGED = [
  { label: "Jan", value: 9 },
  { label: "Feb", value: 8 },
  { label: "Mar", value: 8 },
  { label: "Apr", value: 7 },
  { label: "May", value: 7 },
  { label: "Jun", value: 6 },
];

/**
 * Growth quality (derived) metrics by month. Ratios reconcile with the movement
 * series: quick ratio ≈ gross adds ÷ gross losses, coverage ≈ new ÷ churn, etc.
 */
export const GROWTH_QUICK_RATIO = [
  { label: "Jan", value: 3.2 },
  { label: "Feb", value: 2.8 },
  { label: "Mar", value: 3.6 },
  { label: "Apr", value: 3.1 },
  { label: "May", value: 3.8 },
  { label: "Jun", value: EXAMPLE.quickRatio },
];
export const GROSS_CHURN_COVERAGE = [
  { label: "Jan", value: 3.1 },
  { label: "Feb", value: 2.3 },
  { label: "Mar", value: 3.0 },
  { label: "Apr", value: 2.3 },
  { label: "May", value: 3.1 },
  { label: "Jun", value: 3.3 },
];
export const EXPANSION_VS_CONTRACTION_RATIO = [
  { label: "Jan", value: 3.0 },
  { label: "Feb", value: 1.4 },
  { label: "Mar", value: 4.8 },
  { label: "Apr", value: 1.8 },
  { label: "May", value: 3.7 },
  { label: "Jun", value: 3.8 },
];
export const EXPANSION_TO_CONTRACTION_COUNT_RATIO = [
  { label: "Jan", value: 2.3 },
  { label: "Feb", value: 1.3 },
  { label: "Mar", value: 4.5 },
  { label: "Apr", value: 1.2 },
  { label: "May", value: 2.7 },
  { label: "Jun", value: 2.5 },
];
/** Growth efficiency index (%) — net new ÷ gross positive movements. */
export const GROWTH_EFFICIENCY_INDEX = [
  { label: "Jan", value: 69 },
  { label: "Feb", value: 54 },
  { label: "Mar", value: 73 },
  { label: "Apr", value: 57 },
  { label: "May", value: 72 },
  { label: "Jun", value: 74 },
];
/** Gross-to-net spread (percentage points) — NRR minus GRR. */
export const GROSS_TO_NET_SPREAD = [
  { label: "Jan", value: 7 },
  { label: "Feb", value: 14 },
  { label: "Mar", value: 14 },
  { label: "Apr", value: 16 },
  { label: "May", value: 15 },
  { label: "Jun", value: 15 },
];

/** ARPA by month ($) — total MRR ÷ paying accounts, landing on the example's $200. */
export const ARPA_TREND = [
  { label: "Jan", value: EXAMPLE.arpa },
  { label: "Feb", value: 204 },
  { label: "Mar", value: 206 },
  { label: "Apr", value: 209 },
  { label: "May", value: 212 },
  { label: "Jun", value: 215 },
];

/** ARPU by month ($) — total MRR ÷ related users, lower than ARPA. */
export const ARPU_TREND = [
  { label: "Jan", value: 70 },
  { label: "Feb", value: 72 },
  { label: "Mar", value: 75 },
  { label: "Apr", value: 74 },
  { label: "May", value: 78 },
  { label: "Jun", value: 80 },
];

/** Active accounts by month (count) — accounts with recent activity. */
export const ACTIVE_ACCOUNTS = [
  { label: "Jan", value: 140 },
  { label: "Feb", value: 150 },
  { label: "Mar", value: 155 },
  { label: "Apr", value: 160 },
  { label: "May", value: 165 },
  { label: "Jun", value: 172 },
];

/** Engaged accounts by month (count) — the highly-engaged subset. */
export const ENGAGED_ACCOUNTS = [
  { label: "Jan", value: 60 },
  { label: "Feb", value: 68 },
  { label: "Mar", value: 72 },
  { label: "Apr", value: 76 },
  { label: "May", value: 80 },
  { label: "Jun", value: 86 },
];

/** New paying accounts by month (count) — first-time paid subscriptions. */
export const NEW_PAYING_ACCOUNTS = [
  { label: "Jan", value: 8 },
  { label: "Feb", value: 7 },
  { label: "Mar", value: 6 },
  { label: "Apr", value: 7 },
  { label: "May", value: 5 },
  { label: "Jun", value: 8 },
];

/** Churned paying accounts by month (count) — full cancellations. */
export const CHURNED_PAYING_ACCOUNTS = [
  { label: "Jan", value: 3 },
  { label: "Feb", value: 3 },
  { label: "Mar", value: 3 },
  { label: "Apr", value: 4 },
  { label: "May", value: 3 },
  { label: "Jun", value: 4 },
];

/** Churn acceleration by month (percentage points) — change in churn rate vs prior period. */
export const CHURN_ACCELERATION = [
  { label: "Jan", value: 0.4 },
  { label: "Feb", value: -0.3 },
  { label: "Mar", value: 0.6 },
  { label: "Apr", value: -0.2 },
  { label: "May", value: 0.3 },
  { label: "Jun", value: -0.4 },
];

/** Logo vs dollar churn gap by month (pp) — customer churn rate minus gross MRR churn rate. */
export const LOGO_VS_DOLLAR_CHURN_GAP = [
  { label: "Jan", value: -3 },
  { label: "Feb", value: -5 },
  { label: "Mar", value: -2 },
  { label: "Apr", value: -4 },
  { label: "May", value: -3 },
  { label: "Jun", value: -4 },
];

/** Contraction-before-cancellation (accounts) by month (count). */
export const CONTRACTION_BEFORE_CANCEL_ACCOUNTS = [
  { label: "Jan", value: 4 },
  { label: "Feb", value: 6 },
  { label: "Mar", value: 3 },
  { label: "Apr", value: 5 },
  { label: "May", value: 4 },
  { label: "Jun", value: 5 },
];

/** Average active users per account by month. */
export const AVG_ACTIVE_USERS = [
  { label: "Jan", value: 2.6 },
  { label: "Feb", value: 2.8 },
  { label: "Mar", value: 2.9 },
  { label: "Apr", value: 3.0 },
  { label: "May", value: 3.1 },
  { label: "Jun", value: 3.3 },
];

/** Single-active-user MRR share by month (%) — share of MRR in one-user accounts. */
export const SINGLE_USER_MRR_SHARE = [
  { label: "Jan", value: 42 },
  { label: "Feb", value: 40 },
  { label: "Mar", value: 38 },
  { label: "Apr", value: 37 },
  { label: "May", value: 35 },
  { label: "Jun", value: 33 },
];

/** Single-active-user account count by month. */
export const SINGLE_USER_COUNT = [
  { label: "Jan", value: 55 },
  { label: "Feb", value: 54 },
  { label: "Mar", value: 52 },
  { label: "Apr", value: 50 },
  { label: "May", value: 48 },
  { label: "Jun", value: 46 },
];

/** Paying dormant accounts by month (count) — paying but classified dormant. */
export const PAYING_DORMANT = [
  { label: "Jan", value: 12 },
  { label: "Feb", value: 14 },
  { label: "Mar", value: 13 },
  { label: "Apr", value: 15 },
  { label: "May", value: 14 },
  { label: "Jun", value: 16 },
];

/** Zombie revenue by month ($) — MRR from paying-but-dormant accounts. */
export const ZOMBIE_REVENUE = [
  { label: "Jan", value: 2_400 },
  { label: "Feb", value: 2_800 },
  { label: "Mar", value: 2_600 },
  { label: "Apr", value: 3_000 },
  { label: "May", value: 2_900 },
  { label: "Jun", value: 3_200 },
];

/** Annualized growth rate by month (%) — latest monthly growth compounded over 12 months. */
export const ANNUALIZED_GROWTH_RATE = [
  { label: "Jan", value: 50 },
  { label: "Feb", value: 55 },
  { label: "Mar", value: 52 },
  { label: "Apr", value: 60 },
  { label: "May", value: 58 },
  { label: "Jun", value: 63 },
];
/** Smoothed MRR growth rate, L3M (%). */
export const SMOOTHED_MRR_GROWTH = [
  { label: "Jan", value: 4.8 },
  { label: "Feb", value: 4.5 },
  { label: "Mar", value: 4.6 },
  { label: "Apr", value: 4.4 },
  { label: "May", value: 4.3 },
  { label: "Jun", value: 4.2 },
];
/** Months to double at the latest growth rate. */
export const MONTHS_TO_DOUBLE = [
  { label: "Jan", value: 19 },
  { label: "Feb", value: 18 },
  { label: "Mar", value: 18 },
  { label: "Apr", value: 17 },
  { label: "May", value: 17 },
  { label: "Jun", value: 18 },
];
/** Growth acceleration by month (pp) — change in the MRR growth rate itself. */
export const GROWTH_ACCELERATION = [
  { label: "Jan", value: 0.3 },
  { label: "Feb", value: -0.2 },
  { label: "Mar", value: 0.4 },
  { label: "Apr", value: -0.3 },
  { label: "May", value: 0.2 },
  { label: "Jun", value: -0.1 },
];
/** Growth volatility by month (pp) — std dev of the growth rate across recent buckets. */
export const GROWTH_VOLATILITY = [
  { label: "Jan", value: 1.2 },
  { label: "Feb", value: 1.5 },
  { label: "Mar", value: 1.1 },
  { label: "Apr", value: 1.4 },
  { label: "May", value: 1.0 },
  { label: "Jun", value: 0.9 },
];
/** Expansion velocity by month (%) — new MRR events followed by expansion within 60 days. */
export const EXPANSION_VELOCITY = [
  { label: "Jan", value: 22 },
  { label: "Feb", value: 26 },
  { label: "Mar", value: 24 },
  { label: "Apr", value: 28 },
  { label: "May", value: 27 },
  { label: "Jun", value: 30 },
];
/** Contraction velocity by month (%) — new MRR events followed by contraction/churn within 60 days. */
export const CONTRACTION_VELOCITY = [
  { label: "Jan", value: 12 },
  { label: "Feb", value: 10 },
  { label: "Mar", value: 11 },
  { label: "Apr", value: 9 },
  { label: "May", value: 10 },
  { label: "Jun", value: 8 },
];
/** Reactivation share of gross adds by month (%). */
export const REACTIVATION_SHARE = [
  { label: "Jan", value: 6 },
  { label: "Feb", value: 9 },
  { label: "Mar", value: 4 },
  { label: "Apr", value: 11 },
  { label: "May", value: 7 },
  { label: "Jun", value: 8 },
];
/** Annual share by month (%) — share of MRR on annual contracts. */
export const ANNUAL_SHARE = [
  { label: "Jan", value: 35 },
  { label: "Feb", value: 38 },
  { label: "Mar", value: 40 },
  { label: "Apr", value: 42 },
  { label: "May", value: 45 },
  { label: "Jun", value: 48 },
];
/** Expansion saturation by month (%) — share of paying customers with at least one expansion. */
export const EXPANSION_SATURATION = [
  { label: "Jan", value: 28 },
  { label: "Feb", value: 30 },
  { label: "Mar", value: 33 },
  { label: "Apr", value: 35 },
  { label: "May", value: 37 },
  { label: "Jun", value: 40 },
];
/** Durable MRR by month ($) — current MRR still retained after a year at current churn. */
export const DURABLE_MRR = [
  { label: "Jan", value: 16_000 },
  { label: "Feb", value: 16_500 },
  { label: "Mar", value: 17_000 },
  { label: "Apr", value: 17_500 },
  { label: "May", value: 18_000 },
  { label: "Jun", value: 18_500 },
];

/** Refund rate by month (%) — refunds and credit memos as a share of gross billings. */
export const REFUND_RATE = [
  { label: "Jan", value: 2.1 },
  { label: "Feb", value: 1.8 },
  { label: "Mar", value: 2.4 },
  { label: "Apr", value: 1.9 },
  { label: "May", value: 2.0 },
  { label: "Jun", value: 1.7 },
];
/** Dispute rate by month (%) — chargebacks as a share of gross billings; >1% risks penalties. */
export const DISPUTE_RATE = [
  { label: "Jan", value: 0.6 },
  { label: "Feb", value: 0.8 },
  { label: "Mar", value: 0.5 },
  { label: "Apr", value: 0.9 },
  { label: "May", value: 0.7 },
  { label: "Jun", value: 0.6 },
];
/** MRR discount amount by month ($) — total discount applied, normalised monthly. */
export const MRR_DISCOUNT_AMOUNT = [
  { label: "Jan", value: 1_200 },
  { label: "Feb", value: 1_400 },
  { label: "Mar", value: 1_300 },
  { label: "Apr", value: 1_500 },
  { label: "May", value: 1_350 },
  { label: "Jun", value: 1_450 },
];
/** Discount value by month ($) — discount applied on first payments of new customers. */
export const DISCOUNT_VALUE = [
  { label: "Jan", value: 600 },
  { label: "Feb", value: 800 },
  { label: "Mar", value: 700 },
  { label: "Apr", value: 900 },
  { label: "May", value: 750 },
  { label: "Jun", value: 850 },
];
/** Discounted first-payment rate by month (%) — new customers whose first payment was discounted. */
export const DISCOUNTED_FIRST_PAYMENT_RATE = [
  { label: "Jan", value: 45 },
  { label: "Feb", value: 42 },
  { label: "Mar", value: 48 },
  { label: "Apr", value: 40 },
  { label: "May", value: 44 },
  { label: "Jun", value: 38 },
];
/** Discounted new accounts by month (count). */
export const DISCOUNTED_NEW_ACCOUNTS = [
  { label: "Jan", value: 9 },
  { label: "Feb", value: 8 },
  { label: "Mar", value: 10 },
  { label: "Apr", value: 7 },
  { label: "May", value: 9 },
  { label: "Jun", value: 8 },
];
/** Discounted new companies by month (count). */
export const DISCOUNTED_NEW_COMPANIES = [
  { label: "Jan", value: 5 },
  { label: "Feb", value: 4 },
  { label: "Mar", value: 6 },
  { label: "Apr", value: 4 },
  { label: "May", value: 5 },
  { label: "Jun", value: 4 },
];
/** Average new-accounts discount size by month ($). */
export const AVG_NEW_ACCOUNTS_DISCOUNT = [
  { label: "Jan", value: 65 },
  { label: "Feb", value: 90 },
  { label: "Mar", value: 70 },
  { label: "Apr", value: 110 },
  { label: "May", value: 80 },
  { label: "Jun", value: 95 },
];
/** Average new-companies discount size by month ($). */
export const AVG_NEW_COMPANIES_DISCOUNT = [
  { label: "Jan", value: 120 },
  { label: "Feb", value: 150 },
  { label: "Mar", value: 130 },
  { label: "Apr", value: 170 },
  { label: "May", value: 140 },
  { label: "Jun", value: 160 },
];

export interface CohortRow {
  label: string;
  /** Retention % by tenure month (index 0 = M0). Shorter arrays = younger cohorts. */
  values: number[];
}

/** Tenure column labels shared by the cohort heatmaps. */
export const COHORT_TENURE = ["M0", "M1", "M2", "M3", "M4", "M5"];

/** NRR by acquisition cohort — starts at 100%, dips as early churn bites, then
 *  recovers above 100% as expansion takes over. Weaker cohorts stay under water. */
export const NRR_COHORT: CohortRow[] = [
  { label: "Jan", values: [100, 98, 101, 104, 103, 107] },
  { label: "Feb", values: [100, 97, 99, 102, 105] },
  { label: "Mar", values: [100, 99, 96, 101] },
  { label: "Apr", values: [100, 98, 102] },
  { label: "May", values: [100, 95] },
  { label: "Jun", values: [100] },
];

/** Customer lifetime value by month ($) — projected, landing on the example's $6,000. */
export const CLV_TREND = [
  { label: "Jan", value: 5_200 },
  { label: "Feb", value: 5_500 },
  { label: "Mar", value: 5_800 },
  { label: "Apr", value: 5_600 },
  { label: "May", value: 5_900 },
  { label: "Jun", value: EXAMPLE.ltv },
];

/** Customer churn rate by month (%) — landing on the example's 6%. */
export const CUSTOMER_CHURN_RATE = [
  { label: "Jan", value: 6.5 },
  { label: "Feb", value: 6.0 },
  { label: "Mar", value: 6.8 },
  { label: "Apr", value: 5.5 },
  { label: "May", value: 6.2 },
  { label: "Jun", value: EXAMPLE.customerChurnRate },
];

/** Total ARR by month — annualised MRR_TREND (× 12), landing on $300,000. */
export const ARR_TREND = MRR_TREND.map((point) => ({
  label: point.label,
  value: point.value * 12,
}));

/**
 * Gross Revenue Retention by month — always below the 100% ceiling (GRR only
 * counts losses), hovering in the high 80s–low 90s and landing on the running
 * example's 90%.
 */
export const GRR_RATE = [
  { label: "Jan", value: 91 },
  { label: "Feb", value: 88 },
  { label: "Mar", value: 90 },
  { label: "Apr", value: 87 },
  { label: "May", value: 92 },
  { label: "Jun", value: EXAMPLE.grr },
];

/** GRR vs NRR, expressed as a percentage of starting revenue. */
export const RETENTION = [
  { label: "GRR", value: EXAMPLE.grr, kind: "gross" as const },
  { label: "NRR", value: EXAMPLE.nrr, kind: "net" as const },
];

/** LTV vs CAC, in dollars. */
export const LTV_CAC = [
  { label: "LTV", value: EXAMPLE.ltv, kind: "ltv" as const },
  { label: "CAC", value: EXAMPLE.cac, kind: "cac" as const },
];

/**
 * Quick Ratio: revenue gained vs revenue lost, each split into its components
 * for a stacked bar.
 */
export const QUICK_RATIO = [
  {
    label: "Gained",
    new: EXAMPLE.newMrr,
    expansion: EXAMPLE.expansionMrr,
    lost: 0,
    contraction: 0,
  },
  {
    label: "Lost",
    new: 0,
    expansion: 0,
    lost: EXAMPLE.churnedMrr,
    contraction: EXAMPLE.contractionMrr,
  },
];
